package world.phantasmal.psolib.asm

import world.phantasmal.core.fastIsWhitespace
import world.phantasmal.core.fastReplace
import world.phantasmal.core.getCodePointAt
import world.phantasmal.core.isDigit

private val HEX_INT_REGEX = Regex("""^-?0[xX][0-9a-fA-F]+$""")
private val FLOAT_REGEX = Regex("""^-?\d+(\.\d+)?(e-?\d+)?$""")

enum class Token {
    Int32,
    Float32,
    InvalidNumber,
    Register,
    Label,
    CodeSection,
    DataSection,
    StrSection,
    InvalidSection,
    Str,
    UnterminatedStr,
    Ident,
    InvalidIdent,
    ArgSeparator,
}

class LineTokenizer {
    private var line = ""
    private var index = 0
    private var startIndex = 0

    var value: Any? = null
        private set

    var type: Token? = null
        private set

    val col: Int get() = startIndex + 1
    val len: Int get() = index - startIndex

    fun tokenize(line: String) {
        this.line = line
        index = 0
        startIndex = 0
    }

    val intValue: Int
        get() {
            require(type === Token.Int32 || type === Token.Register || type === Token.Label)
            return value as Int
        }

    val floatValue: Float
        get() {
            require(type === Token.Float32)
            return value as Float
        }

    val strValue: String
        get() {
            require(
                type === Token.Str ||
                        type === Token.UnterminatedStr ||
                        type === Token.Ident ||
                        type === Token.InvalidIdent
            )
            return value as String
        }

    fun nextToken(): Boolean {
        type = null
        value = null

        while (hasNext()) {
            startIndex = index
            val char = peek()

            if (char == '/') {
                skip()

                if (peek() == '/') {
                    // It's a comment.
                    break
                } else {
                    back()
                }
            }

            if (char.fastIsWhitespace()) {
                skip()
                continue
            }

            if (char == '-' || char.isDigit()) {
                tokenizeNumberOrLabel()
            } else if (char == ',') {
                type = Token.ArgSeparator
                skip()
            } else if (char == '.') {
                tokenizeSection()
            } else if (char == '"') {
                tokenizeString()
            } else if (char == 'r') {
                tokenizeRegisterOrIdent()
            } else {
                tokenizeIdent()
            }

            break
        }

        return if (type == null) {
            startIndex = line.length
            index = line.length
            false
        } else {
            true
        }
    }

    private fun hasNext(): Boolean = index < line.length

    private fun next(): Char = line[index++]

    private fun peek(): Char = line[index]

    private fun skip() {
        index++
    }

    private fun back() {
        index--
    }

    private fun slice(from: Int = 0, to: Int = 0): String =
        line.substring(startIndex + from, index - to)

    private fun eatRestOfToken() {
        while (hasNext()) {
            val char = next()

            if (char == ',' || char.fastIsWhitespace()) {
                back()
                break
            }
        }
    }

    private fun tokenizeNumberOrLabel() {
        val firstChar = next()
        var isLabel = false
        // Track if we've seen '-' followed by '0' for negative hex detection.
        val isNegative = firstChar == '-'

        while (hasNext()) {
            val char = peek()

            if (char == '.' || char == 'e') {
                tokenizeFloat()
                return
            } else if ((char == 'x' || char == 'X') &&
                (firstChar == '0' || (isNegative && slice() == "-0"))
            ) {
                tokenizeHexNumber()
                return
            } else if (char == ':') {
                isLabel = true
                break
            } else if (char == ',' || char.fastIsWhitespace()) {
                break
            } else {
                skip()
            }
        }

        value = slice().toIntOrNull()

        if (isLabel) {
            skip()
        }

        type = when {
            value == null -> Token.InvalidNumber
            isLabel -> Token.Label
            else -> Token.Int32
        }
    }

    private fun tokenizeHexNumber() {
        eatRestOfToken()
        val hexStr = slice()

        if (HEX_INT_REGEX.matches(hexStr)) {
            val negative = hexStr.startsWith('-')
            val digits = if (negative) hexStr.drop(3) else hexStr.drop(2) // drop "-0x" or "0x"
            // Use toUIntOrNull instead of toLongOrNull to avoid Kotlin/JS Long emulation issues.
            val parsed = digits.toUIntOrNull(16)

            if (parsed != null) {
                if (negative && parsed > 0x80000000u) {
                    // Negating values > 0x80000000 would overflow a 32-bit signed integer.
                    type = Token.InvalidNumber
                    return
                }
                value = if (negative) {
                    // -0x80000000 = Int.MIN_VALUE; handle explicitly to avoid overflow.
                    if (parsed == 0x80000000u) Int.MIN_VALUE else -(parsed.toInt())
                } else {
                    parsed.toInt()
                }
                type = Token.Int32
                return
            }
        }

        type = Token.InvalidNumber
    }

    private fun tokenizeFloat() {
        eatRestOfToken()
        val floatStr = slice()

        if (FLOAT_REGEX.matches(floatStr)) {
            value = floatStr.toFloatOrNull()

            if (value != null) {
                type = Token.Float32
                return
            }
        }

        type = Token.InvalidNumber
    }

    private fun tokenizeRegisterOrIdent() {
        skip()
        var isRegister = false

        while (hasNext()) {
            val char = peek()

            if (char.isDigit()) {
                isRegister = true
                skip()
            } else {
                break
            }
        }

        if (isRegister) {
            value = slice(from = 1).toInt()
            type = Token.Register
        } else {
            back()
            tokenizeIdent()
        }
    }

    private fun tokenizeSection() {
        while (hasNext()) {
            if (peek().fastIsWhitespace()) {
                break
            } else {
                skip()
            }
        }

        type = when (slice()) {
            ".code" -> Token.CodeSection
            ".data" -> Token.DataSection
            ".string" -> Token.StrSection
            else -> Token.InvalidSection
        }
    }

    private fun tokenizeString() {
        skip()
        var prevWasBackSpace = false
        var terminated = false

        loop@ // Use label as workaround for https://youtrack.jetbrains.com/issue/KT-43943.
        while (hasNext()) {
            when (peek()) {
                '\\' -> {
                    prevWasBackSpace = true
                }
                '"' -> {
                    if (!prevWasBackSpace) {
                        skip()
                        terminated = true
                        break@loop
                    }

                    prevWasBackSpace = false
                }
                else -> {
                    prevWasBackSpace = false
                }
            }

            skip()
        }

        value = slice(from = 1, to = if (terminated) 1 else 0)
            .fastReplace("\\\"", "\"")
            .fastReplace("\\n", "\n")

        type = if (terminated) {
            Token.Str
        } else {
            Token.UnterminatedStr
        }
    }

    private fun tokenizeIdent() {
        while (hasNext()) {
            val char = peek()

            if (char == ',' || char.fastIsWhitespace()) {
                break
            } else if (char == '/') {
                skip()

                if (peek() == '/') {
                    back()
                    break
                }
            } else {
                skip()
            }
        }

        val ident = slice()
        value = ident

        if (ident.getCodePointAt(0) !in 'a'.code..'z'.code) {
            type = Token.InvalidIdent
            return
        }

        for (i in 1 until ident.length) {
            when (ident.getCodePointAt(i)) {
                in '0'.code..'9'.code,
                in 'a'.code..'z'.code,
                '_'.code,
                '='.code,
                '<'.code,
                '>'.code,
                '!'.code,
                -> {
                    // Valid character.
                }
                else -> {
                    type = Token.InvalidIdent
                    return
                }
            }
        }

        type = Token.Ident
    }
}
